import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { fetchWithTimeout, isTimeoutError, isNetworkError } from '../utils/networkError';
import ApiErrorAlert from '../components/ApiErrorAlert';
import { formatDateWithWeekday, formatTime } from '../utils/dateFormat';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

const AppointmentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token, loading: authLoading, isAuthenticated } = useAuth();

  const [appointment, setAppointment] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiError, setApiError] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [isDownloadingReceipt, setIsDownloadingReceipt] = useState(false);

  // Cancellation state
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancellationPolicy, setCancellationPolicy] = useState(null);
  const [refundInfo, setRefundInfo] = useState(null);
  const [cancellationReason, setCancellationReason] = useState('');

  // Reschedule state
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [rescheduleError, setRescheduleError] = useState(null);
  const [rescheduleSuccess, setRescheduleSuccess] = useState(false);

  // Fetch appointment details
  const fetchAppointment = useCallback(async () => {
    if (!isAuthenticated) return;

    setIsLoading(true);
    setError(null);
    setApiError(null);
    setNotFound(false);

    try {
            const response = await fetchWithTimeout(
        `${API_URL}/appointments/${id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        },
        15000
      );

      if (response.status === 404) {
        setNotFound(true);
        setAppointment(null);
        return;
      }

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to load appointment');
      }

      const data = await response.json();
      setAppointment(data.appointment);
    } catch (err) {
      console.error('Error fetching appointment:', err);
      setApiError(err);

      if (isTimeoutError(err)) {
        setError('Request timed out. Please try again.');
      } else if (isNetworkError(err)) {
        setError('Unable to connect to server. Please check your connection.');
      } else {
        setError(err.message);
      }
    } finally {
      setIsLoading(false);
      setIsRetrying(false);
    }
  }, [id, isAuthenticated]);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
      return;
    }

    if (isAuthenticated) {
      fetchAppointment();
    }
  }, [authLoading, isAuthenticated, fetchAppointment, navigate]);

  const handleRetry = useCallback(() => {
    setIsRetrying(true);
    fetchAppointment();
  }, [fetchAppointment]);

  // Fetch cancellation policy and calculate refund
  const fetchCancellationPolicy = useCallback(async (doctorId) => {
    try {
      const response = await fetchWithTimeout(
        `${API_URL}/doctors/${doctorId}/cancellation-policy`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        },
        10000
      );

      if (response.ok) {
        const data = await response.json();
        return data.policy;
      }
      // Return default policy if not found
      return {
        freeCancellationHours: 24,
        partialRefundPercentage: 50,
        partialRefundHours: 2,
        noRefundHours: 2
      };
    } catch (err) {
      console.error('Error fetching cancellation policy:', err);
      // Return default policy on error
      return {
        freeCancellationHours: 24,
        partialRefundPercentage: 50,
        partialRefundHours: 2,
        noRefundHours: 2
      };
    }
  }, [token]);

  // Calculate refund based on time until appointment and policy
  const calculateRefund = useCallback((appointmentStartTime, policy, paymentAmount) => {
    const now = new Date();
    const appointmentTime = new Date(appointmentStartTime);
    const hoursUntilAppointment = (appointmentTime - now) / (1000 * 60 * 60);

    const amount = parseFloat(paymentAmount) || 0;

    if (hoursUntilAppointment >= policy.freeCancellationHours) {
      return {
        refundPercentage: 100,
        refundAmount: amount,
        refundType: 'full',
        message: `Full refund of £${amount.toFixed(2)} (cancelling ${Math.floor(hoursUntilAppointment)} hours before appointment)`
      };
    } else if (hoursUntilAppointment >= policy.partialRefundHours) {
      const refundAmount = amount * (policy.partialRefundPercentage / 100);
      return {
        refundPercentage: policy.partialRefundPercentage,
        refundAmount: refundAmount,
        refundType: 'partial',
        message: `${policy.partialRefundPercentage}% refund of £${refundAmount.toFixed(2)} (cancelling ${Math.floor(hoursUntilAppointment)} hours before appointment)`
      };
    } else {
      return {
        refundPercentage: 0,
        refundAmount: 0,
        refundType: 'none',
        message: `No refund available (cancelling less than ${policy.noRefundHours} hours before appointment)`
      };
    }
  }, []);

  // Open cancel modal
  const handleOpenCancelModal = async () => {
    if (!appointment) return;

    // Fetch cancellation policy
    const policy = await fetchCancellationPolicy(appointment.doctor_id);
    setCancellationPolicy(policy);

    // Calculate refund if payment was made
    if (appointment.payment_status === 'paid' && appointment.payment_amount) {
      const refund = calculateRefund(appointment.start_time, policy, appointment.payment_amount);
      setRefundInfo(refund);
    } else {
      setRefundInfo(null);
    }

    setShowCancelModal(true);
  };

  // Cancel appointment handler
  const handleCancelAppointment = async () => {
    setIsCancelling(true);
    setError(null);

    try {
      const response = await fetchWithTimeout(
        `${API_URL}/appointments/${id}/cancel`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ reason: cancellationReason || 'Cancelled by user' })
        },
        10000
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to cancel appointment');
      }

      // Refresh appointment details
      await fetchAppointment();
      setShowCancelModal(false);
      setCancellationReason('');
    } catch (err) {
      console.error('Cancel appointment error:', err);
      setError(err.message);
    } finally {
      setIsCancelling(false);
    }
  };

  // Delete appointment handler
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to permanently delete this appointment? This action cannot be undone.')) {
      return;
    }

    setIsDeleting(true);

    try {
            const response = await fetchWithTimeout(
        `${API_URL}/appointments/${id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        },
        10000
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete appointment');
      }

      // Redirect back to appropriate dashboard after successful deletion
      if (user?.role === 'doctor') {
        navigate('/doctor/dashboard', { state: { message: 'Appointment deleted successfully' } });
      } else {
        navigate('/patient/appointments', { state: { message: 'Appointment deleted successfully' } });
      }
    } catch (err) {
      console.error('Delete appointment error:', err);
      setError(err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  // Fetch available slots for reschedule
  const fetchAvailableSlots = useCallback(async (date) => {
    if (!appointment || !date) return;

    setIsLoadingSlots(true);
    setAvailableSlots([]);
    setRescheduleError(null);

    try {
      const response = await fetchWithTimeout(
        `${API_URL}/appointments/doctor/${appointment.doctor_id}/slots?date=${date}&serviceId=${appointment.service_id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        },
        15000
      );

      if (!response.ok) {
        throw new Error('Failed to load available slots');
      }

      const data = await response.json();
      setAvailableSlots(data.slots || []);
    } catch (err) {
      console.error('Error fetching slots:', err);
      setRescheduleError('Failed to load available time slots. Please try again.');
    } finally {
      setIsLoadingSlots(false);
    }
  }, [appointment, token]);

  // Handle date selection for reschedule
  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedTime('');
    fetchAvailableSlots(date);
  };

  // Handle reschedule submission
  const handleReschedule = async () => {
    if (!selectedTime) {
      setRescheduleError('Please select a time slot');
      return;
    }

    setIsRescheduling(true);
    setRescheduleError(null);

    try {
      const response = await fetchWithTimeout(
        `${API_URL}/appointments/${id}/reschedule`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ newStartTime: selectedTime })
        },
        15000
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to reschedule appointment');
      }

      setRescheduleSuccess(true);

      // Refresh appointment details after a brief delay
      setTimeout(() => {
        fetchAppointment();
        setShowRescheduleModal(false);
        setRescheduleSuccess(false);
        setSelectedDate('');
        setSelectedTime('');
        setAvailableSlots([]);
      }, 2000);
    } catch (err) {
      console.error('Reschedule error:', err);
      setRescheduleError(err.message);
    } finally {
      setIsRescheduling(false);
    }
  };

  // Open reschedule modal
  const handleOpenRescheduleModal = () => {
    setShowRescheduleModal(true);
    setRescheduleError(null);
    setRescheduleSuccess(false);
    setSelectedDate('');
    setSelectedTime('');
    setAvailableSlots([]);
  };

  // Close reschedule modal
  const handleCloseRescheduleModal = () => {
    setShowRescheduleModal(false);
    setRescheduleError(null);
    setRescheduleSuccess(false);
    setSelectedDate('');
    setSelectedTime('');
    setAvailableSlots([]);
  };

  // Download receipt handler
  const handleDownloadReceipt = async () => {
    setIsDownloadingReceipt(true);
    try {
            const response = await fetchWithTimeout(
        `${API_URL}/appointments/${id}/receipt`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        },
        15000
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to download receipt');
      }

      // Get the HTML content
      const htmlContent = await response.text();

      // Open in a new window for printing/saving
      const receiptWindow = window.open('', '_blank');
      if (receiptWindow) {
        receiptWindow.document.write(htmlContent);
        receiptWindow.document.close();
        console.log('[RECEIPT] Receipt opened in new window');
      } else {
        // Fallback: download as HTML file
        const blob = new Blob([htmlContent], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `receipt_CAL-${id.substring(0, 8).toUpperCase()}.html`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        console.log('[RECEIPT] Receipt downloaded as HTML file');
      }
    } catch (err) {
      console.error('Download receipt error:', err);
      setError(err.message);
    } finally {
      setIsDownloadingReceipt(false);
    }
  };

  // Status badge styling
  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'pending':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'completed':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'cancelled':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      default:
        return 'bg-slate-200 dark:bg-silver/20 text-light-text-secondary dark:text-silver/60 border-slate-300 dark:border-silver/30';
    }
  };

  // Loading state
  if (authLoading || isLoading) {
    return (
      <div className="min-h-[calc(100vh-5rem)] px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-slate-200 dark:bg-silver/20 rounded w-1/3"></div>
            <div className="glass border border-light-border dark:border-border rounded-2xl p-6 space-y-4">
              <div className="h-6 bg-slate-200 dark:bg-silver/20 rounded w-1/2"></div>
              <div className="h-4 bg-slate-200 dark:bg-silver/20 rounded w-3/4"></div>
              <div className="h-4 bg-slate-200 dark:bg-silver/20 rounded w-2/3"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Not found state
  if (notFound) {
    return (
      <div className="min-h-[calc(100vh-5rem)] px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <div className="glass border border-red-500/30 bg-red-500/5 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="font-display text-2xl italic mb-2 text-red-400">
              Appointment Not Found
            </h1>
            <p className="font-body text-light-text-secondary dark:text-silver/60 mb-6">
              This appointment may have been deleted or you don't have permission to view it.
            </p>
            <Link
              to={user?.role === 'doctor' ? '/doctor/dashboard' : '/patient/appointments'}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-silver to-pure-white text-obsidian rounded-xl font-mono text-sm uppercase tracking-wider hover:opacity-90 transition-smooth"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to {user?.role === 'doctor' ? 'Dashboard' : 'Appointments'}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !appointment) {
    return (
      <div className="min-h-[calc(100vh-5rem)] px-4 py-8">
        <div className="max-w-2xl mx-auto">
          {apiError && (
            <ApiErrorAlert
              error={apiError}
              onRetry={handleRetry}
              isRetrying={isRetrying}
            />
          )}
        </div>
      </div>
    );
  }

  if (!appointment) {
    return null;
  }

  const isDoctor = user?.role === 'doctor';
  const patientName = appointment.patient_first_name
    ? `${appointment.patient_first_name} ${appointment.patient_last_name}`
    : appointment.guest_name || 'Guest Patient';
  const doctorName = `Dr. ${appointment.doctor_first_name} ${appointment.doctor_last_name}`;

  return (
    <div className="min-h-[calc(100vh-5rem)] px-4 py-8">
      {/* Cancellation Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-light-card dark:bg-obsidian border border-light-border dark:border-border rounded-2xl p-8 max-w-md w-full">
            <h3 className="font-display text-2xl italic text-light-text dark:text-pure-white mb-2">
              Cancel Appointment
            </h3>
            <p className="text-light-text-secondary dark:text-silver/60 mb-6">
              Are you sure you want to cancel this appointment?
            </p>

            {/* Cancellation Policy */}
            {cancellationPolicy && (
              <div className="mb-6 p-4 bg-slate-100 dark:bg-charcoal/50 border border-light-border dark:border-border rounded-xl">
                <h4 className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-3">
                  Cancellation Policy
                </h4>
                <ul className="space-y-2 text-sm text-slate-600 dark:text-silver/80">
                  <li className="flex items-start gap-2">
                    <span className="text-green-400">&#x2713;</span>
                    <span>Full refund: {cancellationPolicy.freeCancellationHours}+ hours before</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-yellow-400">&#x2713;</span>
                    <span>{cancellationPolicy.partialRefundPercentage}% refund: {cancellationPolicy.partialRefundHours}-{cancellationPolicy.freeCancellationHours} hours before</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-400">&#x2717;</span>
                    <span>No refund: less than {cancellationPolicy.noRefundHours} hours before</span>
                  </li>
                </ul>
              </div>
            )}

            {/* Refund Information */}
            {refundInfo && (
              <div className={`mb-6 p-4 rounded-xl border ${
                refundInfo.refundType === 'full'
                  ? 'bg-green-500/10 border-green-500/30'
                  : refundInfo.refundType === 'partial'
                  ? 'bg-yellow-500/10 border-yellow-500/30'
                  : 'bg-red-500/10 border-red-500/30'
              }`} data-testid="refund-info">
                <h4 className={`font-mono text-xs uppercase tracking-widest mb-2 ${
                  refundInfo.refundType === 'full'
                    ? 'text-green-400'
                    : refundInfo.refundType === 'partial'
                    ? 'text-yellow-400'
                    : 'text-red-400'
                }`}>
                  Refund Amount
                </h4>
                <p className="font-display text-xl italic text-light-text dark:text-pure-white mb-1">
                  £{refundInfo.refundAmount.toFixed(2)}
                  <span className="text-sm font-body text-slate-500 dark:text-silver/60 ml-2">
                    ({refundInfo.refundPercentage}% refund)
                  </span>
                </p>
                <p className="text-sm text-slate-500 dark:text-silver/60">
                  {refundInfo.message}
                </p>
              </div>
            )}

            {/* Cancellation Reason */}
            <div className="mb-6">
              <label className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 block mb-2">
                Reason for cancellation (optional)
              </label>
              <textarea
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                placeholder="Let us know why you're cancelling..."
                className="w-full px-4 py-3 bg-slate-100 dark:bg-charcoal border border-light-border dark:border-border rounded-xl font-body text-light-text dark:text-pure-white placeholder-slate-400 dark:placeholder-silver/40 focus:border-slate-400 dark:focus:border-silver/40 focus:outline-none transition-smooth resize-none"
                rows={3}
                data-testid="cancellation-reason"
              />
            </div>

            {/* Error message */}
            {error && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl">
                <p className="text-red-400 font-mono text-xs">{error}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-4">
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setCancellationReason('');
                  setError(null);
                }}
                disabled={isCancelling}
                className="flex-1 py-3 px-6 border border-light-border dark:border-border rounded-xl text-slate-500 dark:text-silver hover:bg-slate-100 dark:hover:bg-silver/5 transition-smooth disabled:opacity-50"
              >
                Keep Appointment
              </button>
              <button
                onClick={handleCancelAppointment}
                disabled={isCancelling}
                className="flex-1 py-3 px-6 bg-red-500/20 border border-red-500/30 rounded-xl text-red-400 hover:bg-red-500/30 transition-smooth disabled:opacity-50"
                data-testid="confirm-cancel-button"
              >
                {isCancelling ? 'Cancelling...' : 'Confirm Cancellation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reschedule Modal */}
      {showRescheduleModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-light-card dark:bg-obsidian border border-light-border dark:border-border rounded-2xl p-8 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h3 className="font-display text-2xl italic text-light-text dark:text-pure-white mb-2">
              Reschedule Appointment
            </h3>
            <p className="text-light-text-secondary dark:text-silver/60 mb-6">
              Select a new date and time for your appointment.
            </p>

            {/* Success message */}
            {rescheduleSuccess && (
              <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
                <div className="flex items-center gap-3">
                  <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <div>
                    <p className="text-green-400 font-medium">Appointment Rescheduled!</p>
                    <p className="text-green-400/80 text-sm">A notification has been sent to confirm the new time.</p>
                  </div>
                </div>
              </div>
            )}

            {!rescheduleSuccess && (
              <>
                {/* Date Selection */}
                <div className="mb-6">
                  <label className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 block mb-2">
                    Select New Date
                  </label>
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => handleDateSelect(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 bg-slate-100 dark:bg-charcoal border border-light-border dark:border-border rounded-xl font-body text-light-text dark:text-pure-white focus:border-slate-400 dark:focus:border-silver/40 focus:outline-none transition-smooth"
                    data-testid="reschedule-date-input"
                  />
                </div>

                {/* Time Slots */}
                {selectedDate && (
                  <div className="mb-6">
                    <label className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 block mb-2">
                      Select New Time
                    </label>
                    {isLoadingSlots ? (
                      <div className="flex items-center justify-center py-8">
                        <svg className="animate-spin h-6 w-6 text-silver" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                      </div>
                    ) : availableSlots.length > 0 ? (
                      <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto p-1">
                        {availableSlots.map((slot) => (
                          <button
                            key={slot.time}
                            onClick={() => setSelectedTime(slot.time)}
                            className={`py-2 px-3 rounded-lg font-mono text-sm transition-smooth ${
                              selectedTime === slot.time
                                ? 'bg-green-500/20 border-2 border-green-500 text-green-400'
                                : 'bg-slate-100 dark:bg-charcoal border border-light-border dark:border-border hover:border-slate-400 dark:hover:border-silver/40 text-light-text dark:text-pure-white'
                            }`}
                            data-testid={`time-slot-${slot.display}`}
                          >
                            {slot.display}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-light-text-secondary dark:text-silver/60">
                        <svg className="w-10 h-10 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-sm">No available slots on this date</p>
                        <p className="text-xs mt-1">Please select a different date</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Selected Time Display */}
                {selectedTime && (
                  <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
                    <p className="text-green-400 font-mono text-sm">
                      New appointment: {selectedDate} at {availableSlots.find(s => s.time === selectedTime)?.display}
                    </p>
                  </div>
                )}

                {/* Error message */}
                {rescheduleError && (
                  <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl">
                    <p className="text-red-400 font-mono text-xs">{rescheduleError}</p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-4">
                  <button
                    onClick={handleCloseRescheduleModal}
                    disabled={isRescheduling}
                    className="flex-1 py-3 px-6 border border-light-border dark:border-border rounded-xl text-slate-500 dark:text-silver hover:bg-slate-100 dark:hover:bg-silver/5 transition-smooth disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleReschedule}
                    disabled={isRescheduling || !selectedTime}
                    className="flex-1 py-3 px-6 bg-green-500/20 border border-green-500/30 rounded-xl text-green-400 hover:bg-green-500/30 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="confirm-reschedule-button"
                  >
                    {isRescheduling ? 'Rescheduling...' : 'Confirm Reschedule'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div className="max-w-2xl mx-auto">
        {/* Back link */}
        <Link
          to={isDoctor ? '/doctor/dashboard' : '/patient/appointments'}
          className="inline-flex items-center gap-2 text-light-text-secondary dark:text-silver/60 hover:text-light-text dark:hover:text-silver transition-smooth mb-6"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          <span className="font-mono text-xs uppercase tracking-widest">
            Back to {isDoctor ? 'Dashboard' : 'Appointments'}
          </span>
        </Link>

        {/* Header */}
        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="font-display text-3xl italic tracking-tighter mb-2">
              Appointment Details
            </h1>
            <p className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60">
              ID: {appointment.id.substring(0, 8)}...
            </p>
          </div>
          <span className={`px-3 py-1 rounded-lg border font-mono text-xs uppercase ${getStatusColor(appointment.status)}`}>
            {appointment.status}
          </span>
        </div>

        {/* Error message */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
            <p className="text-red-400 font-mono text-sm">{error}</p>
          </div>
        )}

        {/* Appointment info card */}
        <div className="glass border border-light-border dark:border-border rounded-2xl p-6 mb-6">
          {/* Date & Time */}
          <div className="mb-6">
            <h2 className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-2">
              Date & Time
            </h2>
            <p className="font-display text-xl italic">
              {formatDateWithWeekday(appointment.start_time)}
            </p>
            <p className="font-body text-lg text-slate-500 dark:text-silver/80 mt-1">
              {formatTime(appointment.start_time)} - {formatTime(appointment.end_time)}
            </p>
          </div>

          {/* Service */}
          <div className="mb-6">
            <h2 className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-2">
              Service
            </h2>
            <p className="font-body text-lg">
              {appointment.service_name}
            </p>
            <p className="font-mono text-sm text-light-text-secondary dark:text-silver/60 mt-1">
              {appointment.duration_minutes} minutes • £{appointment.service_price}
            </p>
          </div>

          {/* Patient/Doctor info */}
          <div className="mb-6">
            <h2 className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-2">
              {isDoctor ? 'Patient' : 'Doctor'}
            </h2>
            <p className="font-body text-lg">
              {isDoctor ? patientName : doctorName}
            </p>
            {isDoctor && appointment.guest_email && (
              <p className="font-mono text-sm text-light-text-secondary dark:text-silver/60 mt-1">
                {appointment.guest_email}
              </p>
            )}
            {isDoctor && appointment.guest_phone && (
              <p className="font-mono text-sm text-light-text-secondary dark:text-silver/60">
                {appointment.guest_phone}
              </p>
            )}
          </div>

          {/* Reason for visit */}
          {appointment.reason_for_visit && (
            <div className="mb-6">
              <h2 className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-2">
                Reason for Visit
              </h2>
              <p className="font-body text-slate-500 dark:text-silver/80">
                {appointment.reason_for_visit}
              </p>
            </div>
          )}

          {/* Payment Status */}
          <div>
            <h2 className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-2">
              Payment Status
            </h2>
            <div className="flex items-center justify-between">
              <p className={`font-mono text-sm uppercase ${
                appointment.payment_status === 'paid'
                  ? 'text-green-400'
                  : appointment.payment_status === 'pending'
                  ? 'text-yellow-400'
                  : 'text-light-text-secondary dark:text-silver/60'
              }`}>
                {appointment.payment_status || 'Unknown'}
                {appointment.payment_amount && ` • £${appointment.payment_amount}`}
              </p>
              {/* Download Receipt button - only for paid appointments */}
              {appointment.payment_status === 'paid' && (
                <button
                  onClick={handleDownloadReceipt}
                  disabled={isDownloadingReceipt}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/30 rounded-lg font-mono text-xs uppercase tracking-wider text-green-400 hover:bg-green-500/20 transition-smooth disabled:opacity-50"
                  aria-label="Download payment receipt"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  {isDownloadingReceipt ? 'Loading...' : 'Receipt'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-4">
          {/* Refresh button */}
          <button
            onClick={handleRetry}
            disabled={isRetrying}
            className="flex-1 px-6 py-3 border border-light-border dark:border-border rounded-xl font-mono text-sm uppercase tracking-wider hover:border-slate-400 dark:hover:border-white/20 transition-smooth disabled:opacity-50"
          >
            {isRetrying ? 'Refreshing...' : 'Refresh'}
          </button>

          {/* Reschedule button - for pending/confirmed appointments */}
          {(appointment.status === 'pending' || appointment.status === 'confirmed') && (
            <button
              onClick={handleOpenRescheduleModal}
              className="px-6 py-3 bg-blue-500/10 border border-blue-500/30 rounded-xl font-mono text-sm uppercase tracking-wider text-blue-400 hover:bg-blue-500/20 transition-smooth"
              data-testid="reschedule-appointment-button"
            >
              Reschedule
            </button>
          )}

          {/* Cancel button - for pending/confirmed appointments */}
          {(appointment.status === 'pending' || appointment.status === 'confirmed') && (
            <button
              onClick={handleOpenCancelModal}
              className="px-6 py-3 bg-yellow-500/10 border border-yellow-500/30 rounded-xl font-mono text-sm uppercase tracking-wider text-yellow-400 hover:bg-yellow-500/20 transition-smooth"
              data-testid="cancel-appointment-button"
            >
              Cancel Appointment
            </button>
          )}

          {/* Delete button (for doctors only, for cancelled appointments or testing) */}
          {isDoctor && (
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="px-6 py-3 bg-red-500/10 border border-red-500/30 rounded-xl font-mono text-sm uppercase tracking-wider text-red-400 hover:bg-red-500/20 transition-smooth disabled:opacity-50"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </button>
          )}
        </div>

        {/* Cancellation info */}
        {appointment.status === 'cancelled' && appointment.cancelled_at && (
          <div className="mt-6 p-4 bg-red-500/5 border border-red-500/20 rounded-xl">
            <h3 className="font-mono text-xs uppercase tracking-widest text-red-400/80 mb-2">
              Cancellation Details
            </h3>
            <p className="font-body text-sm text-light-text-secondary dark:text-silver/60">
              Cancelled on {formatDateWithWeekday(appointment.cancelled_at)}
              {appointment.cancelled_by && ` by ${appointment.cancelled_by}`}
            </p>
            {appointment.cancellation_reason && (
              <p className="font-body text-sm text-light-text-secondary dark:text-silver/60 mt-1">
                Reason: {appointment.cancellation_reason}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AppointmentDetail;
